import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';
import DeleteIcon from '@material-ui/icons/Delete';
import { useDispatch } from 'react-redux';
import { getBase64 } from '../../utils/isDevMode';
import { viewInvoice } from '../../Components/InvoiceTool/InvoiceUtil';
import { InputBaseContainer, InputFileContainer } from '../../molecules';
import { setInvoicePDF } from '../../redux/slices/feesSlice';
import {
  MAX_BASE64_UPLOAD_FILE_SIZE,
  MAX_BASE64_UPLOAD_FILE_SIZE_LABEL,
} from '../../constants';

// Statuses the backend derives once a fee has left the office (see
// fees/serializers.py::_derive_fee_status). 'Completed' is an internal flag an
// employee can toggle without sending, so it deliberately does NOT lock the file.
const SENT_FEE_STATUSES = [
  'Sent',
  'Invoiced in QB',
  'Paid',
  'Paid by Another Method',
  'Void',
];

// A reimbursement fee counts as sent to the client once it is attached to an
// invoice. `invoiceId` / `dateInvoiced` are the authoritative signals; the
// status string is a fallback for payloads that omit them.
export const isFeeSentToClient = fee =>
  !!fee &&
  (!!fee.invoiceId ||
    !!fee.dateInvoiced ||
    SENT_FEE_STATUSES.includes(fee.status));

const LOCKED_HINT =
  'This file has been sent to the client and can no longer be changed.';

interface Props {
  initialValues?: any;
  control: any;
  errors: any;
  schema: any;
  isYupFieldRequired: (field: string, schema: any) => boolean;
  setValue: (name: string, value: any) => void;
  isReadOnly?: boolean;
}

const FeeFileAttachment = ({
  initialValues,
  control,
  errors,
  schema,
  isYupFieldRequired,
  setValue,
  isReadOnly,
}: Props) => {
  const [fileName, setFileName] = useState('');
  const dispatch = useDispatch();

  // Once the fee is sent to the client the attachment is frozen: no re-upload,
  // no delete. Viewing the existing file stays available.
  const isLocked = isReadOnly || isFeeSentToClient(initialValues);

  useEffect(() => {
    if (initialValues && initialValues.fileName) {
      setFileName(initialValues.fileName);
    }
  }, [initialValues]);

  const handleFileChange = e => {
    if (isLocked) return;
    // Oversized / non-PDF files are rejected inside InputFileContainer, so
    // anything arriving here is already within the configured upload limit.
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const { name } = file;
    getBase64(file, result => {
      setValue('file', result.replace(/data:application\/pdf;base64,/, ''));
      setValue('fileName', name);
    });
    setFileName(name);
  };

  const onDelete = () => {
    if (isLocked) return;
    setFileName('');
    setValue('file', null);
    setValue('fileName', null);
  };

  const viewFile = () => {
    dispatch(setInvoicePDF(initialValues.file));
    viewInvoice(initialValues.file, initialValues.fileName, true, dispatch, false);
  };
  return (
    <Wrapper>
      <InputBaseContainer>
        <Controller
          name="file"
          control={control}
          defaultValue={null}
          render={() => (
            <>
              <InputFileContainer
                error={errors.file}
                onChange={handleFileChange}
                accept="application/pdf"
                isRequired={!initialValues ? isYupFieldRequired('file', schema) : false}
                disabled={isLocked}
                maxSize={MAX_BASE64_UPLOAD_FILE_SIZE}
                maxSizeLabel={MAX_BASE64_UPLOAD_FILE_SIZE_LABEL}
                hideFileList
              />
              {isLocked && fileName && <LockedHint>{LOCKED_HINT}</LockedHint>}
              <NameWrapper>
                <StyledFileName>
                  <div>
                    <span>File</span>:
                  </div>

                </StyledFileName>
                <FileNameLink onClick={viewFile}>{fileName}</FileNameLink>
                {fileName && (
                  <IconWrapper
                    $disabled={isLocked}
                    title={isLocked ? LOCKED_HINT : 'Delete file'}
                  >
                    <DeleteIcon onClick={isLocked ? undefined : onDelete} />
                  </IconWrapper>
                )}
              </NameWrapper>
            </>
          )}
        />
      </InputBaseContainer>
    </Wrapper>
  );
};

export default FeeFileAttachment;

const Wrapper = styled.div`
  width: 35%;
`;

const StyledFileName = styled.h5`
  margin: 4px;
  & span {
    color: var(--primary);
  }
`;

const NameWrapper = styled.div`
  display: flex;
`;

const IconWrapper = styled.div<{ $disabled?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  padding-left: 1px;
  color: ${({ $disabled }) =>
    $disabled ? 'var(--neutral-400)' : 'var(--primary)'};

  & svg {
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  }
`;

const LockedHint = styled.div`
  font-size: var(--fs-xs);
  color: var(--neutral-500);
  text-align: left;
  margin-top: var(--space-1);
`;

const FileNameLink = styled.div`
  text-decoration: underline;
  cursor: pointer;
  color: blue;
`;
